import { useQuery } from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import { graphql } from '@/gql';
import projectKeys from './keyFactory';

graphql(`
  fragment BaseFolderEntry on FolderEntry {
    __typename
    id
    name
    parentFolderId
    createdAt
    updatedAt
    isSharedWithCustomer
    isShareable {
      result
      reasons
    }
    isDeletable {
      result
      reasons
    }
    ... on UploadFolderEntry {
      upload {
        id
        privateId
        fileName
        fileNameGenerated
        mimeType
        size
        url
        thumbnailUrl
        source {
          description
          url
        }
        linkedEntities {
          __typename
          ... on Invoice {
            id
            project {
              publicId
            }
            customReference
            reference
          }
          ... on Project {
            publicId
          }
          ... on ChangeProposal {
            privateId
            reference
            customReference
          }
          ... on Proposal {
            id
            customReference
            reference
          }
          ... on PurchaseOrder {
            id
            stringReference: reference
          }
          ... on PurchaseOrderIndirect {
            id
            stringReference: reference
          }
          ... on ScheduledEvent {
            id
            name
          }
          ... on UnresolvedLinkedEntity {
            entityType
          }
        }
      }
    }
    ... on UrlFolderEntry {
      url {
        id
        name
        url
      }
    }
    ... on DocumentFolderEntry {
      document {
        __typename
        id
        privateId
        completedAt
        instance {
          previewToken
        }
      }
    }
    ... on DocumentInstanceFolderEntry {
      documentInstance {
        id
        name
        hasSignatories
        isSigned
      }
    }
  }
`);

const GET_FOLDER_ENTRY = graphql(`
  query GetFolderEntry($id: ID!) {
    folderEntry(id: $id) {
      ...BaseFolderEntry
      ... on DocumentInstanceFolderEntry {
        documentInstance {
          documentHtml
        }
      }
    }
  }
`);

const useGetFolderEntry = (
  projectId: string,
  folderEntryId: string | undefined,
  opts?: {
    enabled?: boolean;
  }
) => {
  const { data, ...rest } = useQuery({
    queryKey: projectKeys.folderEntry(projectId, folderEntryId!),
    queryFn: () => {
      return gqlClient.request(GET_FOLDER_ENTRY, {
        id: folderEntryId!,
      });
    },
    enabled: !!folderEntryId && opts?.enabled,
  });

  return {
    folderEntry: data?.folderEntry,
    data,
    ...rest,
  };
};

export default useGetFolderEntry;
